<template>
  <div
    class="c-outreach-record-selected-section"
    data-testid="outreach-record-selected-section"
  >
    <div
      v-if="modelValue.length"
      class="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-solid tw:px-3 tw:py-2"
      :class="{
        'tw:border-blue-200 tw:bg-blue-100 tw:text-blue-600':
          !showSelectedContactsWarning,
        'tw:border-orange-600 tw:bg-orange-100 tw:text-orange-600':
          showSelectedContactsWarning,
      }"
    >
      <QIcon
        v-if="showSelectedContactsWarning"
        name="ion-alert"
      >
        <BaseStreemTooltip>
          {{
            $t("outreach_record_selected_section.warning_tooltip", {
              limit: CONTACT_LIST_LIMIT,
            })
          }}
        </BaseStreemTooltip>
      </QIcon>
      <i18n-t
        :keypath="`outreach_record_selected_section.${recordKey}`"
        :plural="modelValue.length"
        scope="global"
        tag="span"
      >
        <template #count>
          <strong
            >{{ prettyNumber(Math.min(CONTACT_LIST_LIMIT, modelValue.length)) }}
          </strong>
        </template>
      </i18n-t>
      <QIcon
        name="fas fa-circle"
        size="0.25em"
      />

      <OutreachRecordSelectedSectionLink
        :warning="showSelectedContactsWarning"
        @click="showSelection"
      >
        {{ $t("outreach_record_selected_section.view") }}
      </OutreachRecordSelectedSectionLink>
      <QIcon
        name="fas fa-circle"
        size="0.25em"
      />
      <OutreachRecordSelectedSectionLink
        :warning="showSelectedContactsWarning"
        @click="clearSelection"
      >
        {{ $t("outreach_record_selected_section.clear") }}
      </OutreachRecordSelectedSectionLink>
      <template
        v-if="
          hideRemoveButton !== true &&
          (recordKey === 'contacts' || recordKey === 'private_influencers')
        "
      >
        <QIcon
          name="fas fa-circle"
          size="0.25em"
        />
        <OutreachRecordSelectedSectionLink
          :warning="showSelectedContactsWarning"
          @click="onRemoveContacts"
        >
          {{ $t("outreach_record_selected_section.remove") }}
        </OutreachRecordSelectedSectionLink>
      </template>
      <template v-if="recordKey === 'lists'">
        <QIcon
          name="fas fa-circle"
          size="0.25em"
        />
        <OutreachRecordSelectedSectionLink
          :warning="showSelectedContactsWarning"
          @click="onDeleteLists"
        >
          {{ $t("global.remove") }}
        </OutreachRecordSelectedSectionLink>
      </template>
    </div>
  </div>
</template>

<script>
import { Notify } from "quasar";

import { BaseStreemTooltip } from "shared/components/base";
import { CONTACT_LIST_LIMIT } from "shared/constants";
import { prettyNumber } from "shared/helpers/number";

import OutreachRecordSelectedSectionLink from "./OutreachRecordSelectedSectionLink.vue";

export default {
  name: "OutreachRecordSelectedSection",
  components: { OutreachRecordSelectedSectionLink, BaseStreemTooltip },
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    listId: {
      type: [Number, String],
      default: undefined,
    },
    recordKey: {
      type: String,
      required: true,
      validator(value) {
        return [
          "contacts",
          "lists",
          "mailing_lists",
          "private_influencers",
        ].includes(value);
      },
    },
    hideRemoveButton: {
      type: Boolean,
    },
    viewModal: {
      type: String,
      required: true,
    },
  },
  emits: [
    "clicked-clear",
    "clicked-remove",
    "clicked-view",
    "refresh",
    "update:modelValue",
  ],
  data() {
    return {
      CONTACT_LIST_LIMIT,
    };
  },
  computed: {
    showSelectedContactsWarning() {
      return this.modelValue.length >= CONTACT_LIST_LIMIT;
    },
  },
  methods: {
    prettyNumber,
    showSelection() {
      this.$modals.open(this.viewModal, {
        props: {
          listId: this.listId,
          selectedIds: this.modelValue,
        },
        events: {
          remove: (ids) => {
            this.$emit(
              "update:modelValue",
              this.modelValue.filter((id) => !ids.includes(id))
            );
          },
        },
      });

      this.$emit("clicked-view");
    },
    clearSelection() {
      this.$emit("update:modelValue", []);
      this.$emit("clicked-clear");
    },
    onRemoveContacts() {
      this.$dialog
        .confirm({
          theme: "danger",
          title: this.$t(
            "outreach_contact_lists_contacts.remove_contacts_confirm_title",
            { count: this.modelValue.length }
          ),
          dialogMaxWidth: "350px",
          message: this.$t(
            "outreach_contact_lists_contacts.remove_contacts_confirm_message",
            {
              count: this.modelValue.length,
              name: this.$route.query.listLabel,
            }
          ),
        })
        .onOk(() => {
          this.removeContacts();
        });

      this.$emit("clicked-remove");
    },
    async removeContacts() {
      if (this.recordKey === "private_influencers") {
        await this.removeContactsByType(
          "private_influencers/remove_influencers",
          {
            ids: this.modelValue,
          }
        );
      } else {
        await this.removeContactsByType("contact_lists/remove_contacts", {
          id: this.$route.params.listId,
          contacts: this.modelValue,
        });
      }
    },

    async removeContactsByType(apiEndpoint, params) {
      try {
        await this.$streemApiV1.post(apiEndpoint, { params });

        Notify.create({
          message: this.$t(
            "outreach_contact_lists_contacts.remove_contacts_notification"
          ),
          color: "green",
        });

        this.$emit("update:modelValue", []);
        this.$emit("refresh");
      } catch (error) {
        this.$error(
          this.$t("outreach_contact_lists_contacts.remove_contacts_error"),
          error
        );
      }
    },
    onDeleteLists() {
      this.$dialog
        .confirm({
          theme: "danger",
          title: this.$t("outreach_contact_lists.bulk_delete_confirm_title", {
            count: this.modelValue.length,
          }),
          dialogMaxWidth: "350px",
          message: this.$t(
            "outreach_contact_lists.bulk_delete_confirm_message",
            { count: this.modelValue.length }
          ),
        })
        .onOk(() => {
          this.deleteLists();
        });
    },
    async deleteLists() {
      const count = this.modelValue.length;

      try {
        await this.$streemApiV1.delete("contact_lists/bulk_destroy", {
          params: { contact_list_ids: this.modelValue },
        });

        Notify.create({
          message: this.$t("outreach_contact_lists.bulk_delete_notification", {
            count,
          }),
          color: "green",
        });

        this.$emit("update:modelValue", []);
        this.$emit("refresh");
      } catch (error) {
        this.$error(
          this.$t("outreach_contact_lists.bulk_delete_error", { count }),
          error
        );
      }
    },
  },
};
</script>
